/**
 * Display formatting only — no rounding decisions that could change a payout.
 * Values arrive already calculated by the backend.
 */

const eur = new Intl.NumberFormat('nl-BE', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const eurCompact = new Intl.NumberFormat('nl-BE', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const oneDecimal = new Intl.NumberFormat('nl-BE', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

const plain = new Intl.NumberFormat('nl-BE');

export function formatEUR(value) {
  return eur.format(Number(value) || 0);
}

/** Axis ticks and other places where cents are noise. */
export function formatEURCompact(value) {
  return eurCompact.format(Number(value) || 0);
}

export function formatHours(value) {
  return oneDecimal.format(Number(value) || 0);
}

export function formatCount(value) {
  return plain.format(Number(value) || 0);
}

/** "10u 50m" — how drivers actually talk about a shift. */
export function formatDuration(hours) {
  const total = Math.max(0, Number(hours) || 0);
  const h = Math.floor(total);
  const m = Math.round((total - h) * 60);
  if (m === 60) return `${h + 1}u 00m`;
  return `${h}u ${String(m).padStart(2, '0')}m`;
}

const MONTHS_NL = [
  'januari', 'februari', 'maart', 'april', 'mei', 'juni',
  'juli', 'augustus', 'september', 'oktober', 'november', 'december',
];

/** "2026-08" -> "Augustus 2026" */
export function formatMonthLabel(key, { capitalize = true } = {}) {
  if (!key || !/^\d{4}-\d{2}$/.test(key)) return '';
  const [year, month] = key.split('-');
  const name = MONTHS_NL[Number(month) - 1] || '';
  const label = `${name} ${year}`;
  return capitalize ? label.charAt(0).toUpperCase() + label.slice(1) : label;
}

/** "2026-08-22" -> "22 aug 2026" */
export function formatDateShort(value) {
  if (!value || typeof value !== 'string') return '';
  const [y, m, d] = value.slice(0, 10).split('-');
  if (!y || !m || !d) return value;
  const name = (MONTHS_NL[Number(m) - 1] || '').slice(0, 3);
  return `${Number(d)} ${name} ${y}`;
}

/** Shift the "2026-08" key by n months, staying inside the calendar. */
export function shiftMonthKey(key, delta) {
  if (!key || !/^\d{4}-\d{2}$/.test(key)) return null;
  const [y, m] = key.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

/**
 * Percentage change between two periods. Returns null when there is no
 * meaningful baseline, so callers can hide the badge instead of printing
 * a fake "+100%" against a month with no rides.
 */
export function percentChange(current, previous) {
  const now = Number(current) || 0;
  const before = Number(previous) || 0;
  if (before === 0) return null;
  return ((now - before) / Math.abs(before)) * 100;
}

export function formatPercentChange(pct) {
  if (pct === null || pct === undefined || !Number.isFinite(pct)) return '';
  const sign = pct > 0 ? '+' : '';
  return `${sign}${oneDecimal.format(pct)}%`;
}

/** "2026-W23" -> "wk 23" */
export function formatWeekLabel(key) {
  const match = /^(\d{4})-W(\d{1,2})$/.exec(String(key || ''));
  if (!match) return String(key || '');
  return `wk ${Number(match[2])}`;
}

/** "2026-06-14" -> "14 jun" — compact enough for a dense daily axis. */
export function formatDayLabel(value) {
  if (!value || typeof value !== 'string') return '';
  const [, m, d] = value.slice(0, 10).split('-');
  if (!m || !d) return value;
  return `${Number(d)} ${(MONTHS_NL[Number(m) - 1] || '').slice(0, 3)}`;
}
